import { Text, Stack, Box, Actions } from 'braid-design-system';
import React from 'react';

import { ButtonLink, StaticPageProvider, TextLink } from 'src/components';
import { useLocalized } from 'src/shared/hooks';
import type { ActiveTab, ActiveSubTab } from 'src/shared/types';

import { TRACKING_CODES } from '../../../../shared/constant';
import {
  COMPANY_PROFILES_ENQUIRY,
  COMMUNITY_GUIDELINES_AU,
  COMMUNITY_GUIDELINES_NZ,
} from '../../../../shared/links';

import {
  BringAllOpportunities,
  ImageTextComponent,
  IntroSection,
  ProductPromoTiles,
  WhatsNewBanner,
} from './components';
import { RealReviews } from './components/RealReviews/RealReviews';
import SaveMoney from './images/PriceTag.svg';
import ShortList from './images/Puzzle.svg';
import AuthenticReviews from './images/Review.svg';
import SavedSearchRealReview from './images/ReviewMultiple.svg';
import ComprehensiveModeration from './images/SearchNew.svg';
import SavedSearch from './images/SearchSaved.svg';
import MultipleCandidates from './images/Tools.svg';
import CompanyProfile from './images/Trophy.svg';

import * as styles from './CompanyProfiles.css';

const firstRowTiles = {
  AU: [
    {
      icon: ShortList,
      text: 'Add information such as company mission, values, perks and benefits, diversity and inclusion.',
    },
    {
      icon: CompanyProfile,
      text: 'Show your candidates your culture and values through simple to upload photos and videos.',
    },
    {
      icon: SavedSearch,
      text: 'Easily discoverable on the<br />SEEK Candidate home page & the SEEK app.',
    },
  ],
};
const secondRowTiles = {
  AU: [
    {
      icon: SaveMoney,
      text: 'Company Profiles are<br />completely free.',
    },
    {
      icon: MultipleCandidates,
      text: 'In-house SEEK team<br />dedicated to profile support<br />and troubleshooting.',
    },
  ],
};

const realReviewIcons = [
  {
    icon: AuthenticReviews,
    text: 'Authentic reviews',
  },
  {
    icon: ComprehensiveModeration,
    text: 'Comprehensive moderation',
  },
  {
    icon: SavedSearchRealReview,
    text: 'Respond to your reviews',
  },
];

const StaticPageProps = {
  title: 'Create a free company profile page for your business on SEEK',
  returnUrl: '/products/company-profiles',
  eventName: 'company_profiles_page_displayed',
  currentPage: 'company profiles',
  siteSection: 'products',
  activeTab: 'Products' as ActiveTab,
  activeSubTab: 'Company Profiles' as ActiveSubTab,
  description:
    'Learn how to create a company profile page for your business on SEEK - #1 choice for job seekers and employers.',
  index: true,
  follow: true,
};

export default () => (
  <StaticPageProvider {...StaticPageProps}>
    <Box background="surface">
      <Stack space="xxxlarge">
        <IntroSection title="Where your EVP can be the MVP">
          <Text size="large">
            There&apos;s a new way for you to promote your employer value
            proposition to candidates looking for their next role.
          </Text>
        </IntroSection>
        <ImageTextComponent
          title="Introducing SEEK Company Profiles"
          className={styles.companyProfilesImg}
        >
          <Stack space="large">
            <Text size="large">
              A SEEK company profile is a dedicated space to communicate with
              our <strong>highly engaged candidates</strong> at a critical point
              of their <strong>job seeking journey</strong> on the SEEK site and
              mobile app.
            </Text>
            <Text size="large">
              <strong>Showcase your company’s EVP</strong> and brand personality
              to prospective candidates alongside a comprehensive view of{' '}
              <strong>available opportunities.</strong>
            </Text>
          </Stack>
        </ImageTextComponent>
        <ImageTextComponent
          title="A Profile to help attract top applicants"
          className={styles.attractTopTalentsFirstImage}
          reverse={false}
        >
          <Text size="large">
            Top candidates want to find out more about companies before
            applying. Make a great first impression by showcasing your company
            values, perks and culture.
          </Text>
        </ImageTextComponent>
        <BringAllOpportunities title="All your opportunities in one place">
          <Stack space="gutter">
            <Text size="large">
              Opportunities at your company should be easy to find.
            </Text>
            <Text size="large">
              The Company Profiles ‘Jobs’ tab will show all your open roles in
              one convenient location.
            </Text>
          </Stack>
        </BringAllOpportunities>
        <RealReviews
          title="Real reviews by real employees"
          icons={realReviewIcons}
        >
          <Stack space="gutter">
            <Text size="large">
              Your employees can be your best advocates.{' '}
              <TextLink
                product="candidate"
                country={useLocalized({ AU: 'au', NZ: 'nz' })}
                href={useLocalized({
                  AU: COMMUNITY_GUIDELINES_AU,
                  NZ: COMMUNITY_GUIDELINES_NZ,
                })}
                skipResolvedUrl={true}
                tracking={TRACKING_CODES.terms_link_clicked}
              >
                Reviews are moderated
              </TextLink>{' '}
              and displayed on the &apos;Reviews&apos; tab of your Company
              Profile to give candidates a real sense of the employee experience
              at your company. <br />
              <br />
              Build trust and credibility with potential employees by responding
              to reviews and showcasing what makes your workplace great.
            </Text>
          </Stack>
        </RealReviews>
        <ProductPromoTiles
          title="Creating and managing your Company Profile is simple"
          firstRowTiles={useLocalized(firstRowTiles)}
          secondRowTiles={useLocalized(secondRowTiles)}
        />
        <ImageTextComponent
          title="Want your Company Profile published?"
          className={styles.companyProfilePublishImage}
        >
          <Stack space="large">
            <Text size="large">
              For more information or to express interest in Company Profiles,
              please reach out to the SEEK Team.
            </Text>
            <Actions>
              <ButtonLink
                tracking="post-job-button-clicked"
                href={COMPANY_PROFILES_ENQUIRY}
                trackingProps={{
                  elementText: 'get started',
                  elementDesign: 'button',
                  elementAction: 'directs user to company profile enquiry form',
                  entryPoint: 'company profiles - create new account',
                  actionOrigin: 'bottom button company profile page',
                }}
              >
                Get Started
              </ButtonLink>
            </Actions>
          </Stack>
        </ImageTextComponent>
        <Box
          paddingBottom={{
            mobile: 'large',
            tablet: 'xlarge',
            desktop: 'xxlarge',
          }}
        >
          <WhatsNewBanner />
        </Box>
      </Stack>
    </Box>
  </StaticPageProvider>
);
